import React, {
  ReactElement, useEffect, useLayoutEffect, useState,
} from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router';
import { getTitle, waitForElement, NotFoundPage } from 'slovlex-idsk-react-components';
import { useModalDialogueContext } from 'idsk-react-components';
import {
  AdminTerminCreatePage,
  AdminTerminDetailPage,
  AdminTerminListPage,
  AdminTerminUpdatePage,
  AdminTerminHistoriaZmienPage,
  DetailTerminuPage,
  PublikovanyTerminListPage,
  TezaurusCreatePage,
  TezaurusEditPage,
  TezaurusListPage,
  AdminTerminDoktrinalneDefinicieUpdatePage,
  AdminTerminLegalneDefinicieUpdatePage,
  AdminTerminPoznamkaUpdatePage,
  AdminTerminZaznamySekcieUpdatePage,
  AdminTerminPouzitieVPredpisochUpdatePage,
  AdminTerminPreviazaneTerminyUpdatePage,
} from '../page';
import routes from './routes';
import handleAbsoluteUrlRedirects from './absoluteUrlHandler';
import useFetchProfile from '../hooks/useFetchProfile';
import { useSecurityContext } from '../context/SecurityContext';
import { useUserContext } from '../context/UserContext';
import Layout from '../page/Layout';

const TezauryRouter = (): ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();
  const [parentIsSettingRoute, setParentIsSettingRoute] = useState(true);
  const [anchorIsSettingRoute, setAnchorIsSettingRoute] = useState(true);
  const {
    setAccessToken,
    setOrganizationId,
    accessToken,
    organizationId,
  } = useSecurityContext();
  const { pouzivatel } = useUserContext();

  useFetchProfile();

  const { visible } = useModalDialogueContext();
  useEffect(() => {
    window.parent.postMessage({
      type: `iframe-to-parent-modal-dialogue-${visible ? 'open' : 'close'}`,
    }, '*');
  }, [visible]);

  const onMessage = (event): void => {
    const message = event?.data;
    if (message) {
      if (message.type && message.type === 'parent-to-iframe-internal-route') {
        setParentIsSettingRoute(true);
        navigate(message.route, { replace: true });
      } else if (message.type && message.type === 'pageYOffset') {
        const newHeight = message.portalMainWindowInnerHeight * (2 / 3);
        const modalDialogueWindow = document.querySelector<HTMLElement>('.modal-dialogue-window');
        const iframe = document.querySelector<HTMLElement>('#app');
        if (modalDialogueWindow && iframe) {
          if (iframe.offsetHeight > newHeight) {
            modalDialogueWindow.style.maxHeight = `${newHeight}px`;
          } else {
            modalDialogueWindow.style.maxHeight = `${iframe.offsetHeight - 10}px`;
          }
        }
      } else if (typeof message === 'string' && message.includes('accessToken=')) {
        setAccessToken(message.substring('accessToken='.length));
      } else if (typeof message === 'string' && message.includes('organizationId=')) {
        setOrganizationId(message.substring('organizationId='.length));
      }
    }
  };

  useEffect(() => {
    window.addEventListener('message', onMessage);
    window.addEventListener('click', () => {
      window.parent.postMessage({ type: 'iframe-to-parent-clicked' }, '*');
    });
    window.parent.postMessage({ type: 'iframe-to-parent-initialized' }, '*');
    handleAbsoluteUrlRedirects();
    if (window.self === window.top) {
      window.location.replace(`${process.env.REACT_APP_PORTAL_MAIN_URL}/tezaury`
        + `${location.pathname ? location.pathname : ''}`
        + `${location.search ? location.search : ''}${location.hash ? location.hash : ''}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (document.referrer && document.referrer !== document.location.href
      && location.pathname !== routes.loading
      && parentIsSettingRoute && anchorIsSettingRoute) {
      notifyParent();
      setAnchorIsSettingRoute(false);
    }

    if (parentIsSettingRoute && anchorIsSettingRoute) {
      setParentIsSettingRoute(false);
      return;
    }
    notifyParent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.hash, location.search, location.state]);

  const notifyParent = (): void => {
    waitForElement('#app > *').then(() => {
      window.parent.postMessage({
        type: 'iframe-to-parent-internal-route',
        route: `/tezaury${location.pathname ? location.pathname : ''}`
          + `${location.search ? location.search : ''}${location.hash ? location.hash : ''}`,
        title: getTitle(),
      }, '*');
    });
  };

  if (accessToken === undefined || organizationId === undefined) {
    return null;
  }

  if (accessToken && pouzivatel === null) {
    return null;
  }

  return (
    <Routes>
      <Route path={routes.loading} element={null} />
      <Route path={routes.tezaurus.list} element={<PublikovanyTerminListPage />} />
      <Route path={routes.tezaurus.detail.raw} element={<DetailTerminuPage />} />
      <Route path={routes.tezaurus.admin.list} element={<TezaurusListPage />} />
      <Route path={routes.tezaurus.admin.create} element={<TezaurusCreatePage />} />
      <Route path={routes.tezaurus.admin.edit.raw} element={<TezaurusEditPage />} />
      <Route path={routes.tezaurus.admin.termin.list.raw} element={<AdminTerminListPage />} />
      <Route path={routes.tezaurus.admin.termin.create.raw} element={<AdminTerminCreatePage />} />
      <Route path={routes.tezaurus.admin.termin.detail.raw} element={<AdminTerminDetailPage />} />
      <Route path={routes.tezaurus.admin.termin.edit.termin.raw} element={<AdminTerminUpdatePage />} />
      <Route path={routes.tezaurus.admin.termin.historiaZmien.raw} element={<AdminTerminHistoriaZmienPage />} />
      <Route
        path={routes.tezaurus.admin.termin.edit.doktrinalneDefinicie.raw}
        element={<AdminTerminDoktrinalneDefinicieUpdatePage />}
      />
      <Route
        path={routes.tezaurus.admin.termin.edit.previazaneTerminy.raw}
        element={<AdminTerminPreviazaneTerminyUpdatePage />}
      />
      <Route
        path={routes.tezaurus.admin.termin.edit.legalneDefinicie.raw}
        element={<AdminTerminLegalneDefinicieUpdatePage />}
      />
      <Route
        path={routes.tezaurus.admin.termin.edit.zaznamySekcie.raw}
        element={<AdminTerminZaznamySekcieUpdatePage />}
      />
      <Route
        path={routes.tezaurus.admin.termin.edit.poznamka.raw}
        element={<AdminTerminPoznamkaUpdatePage />}
      />
      <Route
        path={routes.tezaurus.admin.termin.edit.pouzitieVPredpisoch.raw}
        element={<AdminTerminPouzitieVPredpisochUpdatePage />}
      />
      <Route path="*" element={<Layout><NotFoundPage /></Layout>} />
    </Routes>
  );
};

export default TezauryRouter;
